<template>
  <div class="login-container">
    <div class="login-card">
      <!-- Logo/Header -->
      <div class="header-section">
        <div class="logo-icon">👨‍🏫</div>
        <h1>Espace Accompagnateur</h1>
        <p class="subtitle">Connectez-vous pour accéder à votre espace personnel</p>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="error-banner">
        <span class="error-icon">⚠</span>
        <span>{{ errorMessage }}</span>
      </div>

      <!-- Login Form -->
      <form @submit.prevent="handleLogin" class="login-form">
        <div class="form-group">
          <label for="email">Email professionnel</label>
          <input
            id="email"
            v-model="credentials.email"
            type="email"
            placeholder="jean.dupont@exemple.fr"
            required
            :disabled="loading"
          />
        </div>

        <div class="form-group">
          <label for="password">Mot de passe</label>
          <div class="password-input-wrapper">
            <input
              id="password"
              v-model="credentials.password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="Entrez votre mot de passe"
              required
              :disabled="loading"
            />
            <button
              type="button"
              class="toggle-password"
              @click="showPassword = !showPassword"
              tabindex="-1"
            >
              {{ showPassword ? '👁️' : '👁️‍🗨️' }}
            </button>
          </div>
        </div>

        <!-- Remember Me & Forgot Password -->
        <div class="form-options">
          <label class="checkbox-label">
            <input type="checkbox" v-model="rememberMe" />
            <span>Se souvenir de moi</span>
          </label>
          <a href="#" class="forgot-password" @click.prevent="handleForgotPassword">
            Mot de passe oublié ?
          </a>
        </div>

        <!-- Login Button -->
        <button type="submit" class="btn btn-primary" :disabled="loading">
          <span v-if="loading">Connexion en cours...</span>
          <span v-else>Se connecter</span>
        </button>

        <!-- Register Button -->
        <div class="register-link">
          <span>Vous n'avez pas encore de compte ?</span>
          <button type="button" @click="handleRegister" class="btn btn-outline">
            Créer un compte
          </button>
        </div>
      </form>

      <!-- Footer Info -->
      <div class="footer-info">
        <p>
          En vous connectant, vous acceptez nos conditions d'utilisation
          et notre politique de confidentialité.
        </p>
      </div>
    </div>

  </div>
</template>

<script>
import AuthService from '@/services/AuthService'
import { ROLES, ACCOMPANIST_ROLES } from '@/router'

export default {
  name: 'AccompanistLogin',
  data() {
    return {
      credentials: {
        email: '',
        password: '',
      },
      showPassword: false,
      rememberMe: false,
      loading: false,
      errorMessage: '',
    }
  },
  methods: {
    async handleLogin() {
      this.loading = true
      this.errorMessage = ''

      try {
        // Login avec email/password
        const response = await AuthService.login({
          email: this.credentials.email,
          password: this.credentials.password,
        })

        console.log('✓ Connexion réussie:', response)

        // Vérifier le statut d'authentification pour récupérer le rôle
        const authStatus = await AuthService.isAuthenticated()

        if (authStatus.authenticated) {
          const userRoleId = parseInt(authStatus.role)

          // Vérifier si c'est un accompagnant (Enseignant, Psychologue ou Famille)
          if (ACCOMPANIST_ROLES.includes(userRoleId)) {
            // Redirection vers le dashboard accompagnateur
            const redirectPath = this.$route.query.redirect || '/accompagnant/dashboard'
            this.$router.push(redirectPath)
          } else if (userRoleId === ROLES.YOUNG_PERSON) {
            // Si c'est un jeune qui essaie de se connecter
            this.errorMessage = 'Ce compte est un compte jeune. Veuillez utiliser l\'autre page de connexion.'
            await AuthService.logout()
          } else {
            // Rôle inconnu
            this.errorMessage = 'Type de compte non reconnu. Veuillez contacter le support.'
            await AuthService.logout()
          }
        } else {
          this.errorMessage = 'Erreur lors de la connexion. Veuillez réessayer.'
        }
      } catch (error) {
        console.error('Erreur de connexion:', error)

        // Gérer les erreurs
        if (error.response?.data?.detail) {
          if (typeof error.response.data.detail === 'string') {
            this.errorMessage = error.response.data.detail
          } else {
            this.errorMessage = 'Identifiants incorrects. Veuillez réessayer.'
          }
        } else if (error.response?.status === 401) {
          this.errorMessage = 'Email ou mot de passe incorrect.'
        } else if (error.response?.status === 403) {
          this.errorMessage = 'Veuillez vérifier votre email avant de vous connecter.'
        } else {
          this.errorMessage = 'Une erreur est survenue. Veuillez réessayer.'
        }
      } finally {
        this.loading = false
      }
    },

    handleRegister() {
      // Rediriger vers la page d'onboarding accompagnateur
      this.$router.push({ name: 'AccompagnateurOnboarding' })
    },

    handleForgotPassword() {
      // Rediriger vers la page de récupération de mot de passe
      this.$router.push({ name: 'ForgotPassword' })
    },
  },
}
</script>

<style scoped>
/* Container principal */
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  position: relative;
  overflow: hidden;
}

/* Login Card */
.login-card {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  max-width: 450px;
  width: 100%;
  position: relative;
  z-index: 1;
  animation: slideUp 0.6s ease;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header Section */
.header-section {
  text-align: center;
  margin-bottom: 24px;
}

.logo-icon {
  font-size: 52px;
  margin-bottom: 12px;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.header-section h1 {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 6px;
}

.subtitle {
  font-size: 14px;
  color: #546e7a;
  margin: 0;
}

/* Error Banner */
.error-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  border-left: 3px solid #f44336;
  border-radius: 8px;
  color: #c62828;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 16px;
  animation: shake 0.5s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  75% { transform: translateX(10px); }
}

.error-icon {
  font-size: 20px;
}

/* Form Styles */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  margin-left: 4px;
}

.form-group input {
  padding: 12px 14px;
  border: 2px solid #e0e7ee;
  border-radius: 10px;
  font-size: 14px;
  transition: all 0.3s ease;
  background-color: #fafbfc;
  color: #2c3e50;
}

.form-group input:focus {
  outline: none;
  border-color: #FF9800;
  background-color: white;
  box-shadow: 0 0 0 4px rgba(255, 152, 0, 0.1);
}

.form-group input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Password Input */
.password-input-wrapper {
  position: relative;
}

.password-input-wrapper input {
  width: 100%;
  padding-right: 50px;
}

.toggle-password {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  padding: 4px;
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

.toggle-password:hover {
  opacity: 1;
}

/* Form Options */
.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: -8px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #546e7a;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.forgot-password {
  font-size: 14px;
  color: #FF9800;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.3s ease;
}

.forgot-password:hover {
  color: #FF9800;
  text-decoration: underline;
}

/* Buttons */
.btn {
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #FF9800;
  color: hsl(60 9.1% 97.8%);
  box-shadow: 0 4px 15px rgba(255, 152, 0, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: #FF9800;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 152, 0, 0.5);
}

.btn-passkey {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
  color: #2c3e50;
  border: 2px solid #e0e7ee;
}

.btn-passkey:hover:not(:disabled) {
  background: linear-gradient(135deg, #e8eef5 0%, #dce4ec 100%);
  border-color: #FF9800;
  transform: translateY(-2px);
}

.passkey-icon {
  font-size: 20px;
}

.btn-outline {
  background: transparent;
  color: #FF9800;
  border: 2px solid #FF9800;
  font-size: 15px;
  padding: 10px 20px;
}

.btn-outline:hover {
  background: #FF9800;
  border-color: #FF9800;
  color: hsl(60 9.1% 97.8%);
  transform: translateY(-2px);
}

/* Divider */
.divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 24px 0;
  color: #90a4ae;
  font-size: 14px;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid #e0e7ee;
}

.divider span {
  padding: 0 16px;
  font-weight: 600;
}

/* Register Link */
.register-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.register-link span {
  font-size: 14px;
  color: #546e7a;
  font-weight: 500;
}

/* Footer Info */
.footer-info {
  margin-top: 24px;
  text-align: center;
}

.footer-info p {
  font-size: 12px;
  color: #90a4ae;
  line-height: 1.5;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .login-card {
    padding: 24px 20px;
  }

  .header-section h1 {
    font-size: 22px;
  }

  .logo-icon {
    font-size: 48px;
  }

  .form-options {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .btn {
    width: 100%;
    padding: 12px 20px;
  }
}

/* Media query pour les écrans avec hauteur réduite */
@media (max-height: 700px) {
  .login-card {
    padding: 20px;
    max-height: calc(100vh - 30px);
  }

  .header-section {
    margin-bottom: 16px;
  }

  .logo-icon {
    font-size: 44px;
    margin-bottom: 8px;
  }

  .header-section h1 {
    font-size: 22px;
    margin-bottom: 4px;
  }

  .subtitle {
    font-size: 13px;
  }

  .login-form {
    gap: 14px;
    margin-bottom: 16px;
  }

  .form-group {
    gap: 5px;
  }

  .form-group label {
    font-size: 12px;
  }

  .form-group input {
    padding: 10px 12px;
    font-size: 13px;
  }

  .btn {
    padding: 10px 18px;
    font-size: 14px;
  }

  .error-banner {
    padding: 8px 12px;
    font-size: 12px;
    margin-bottom: 12px;
  }

  .footer-info {
    margin-top: 16px;
  }

  .footer-info p {
    font-size: 11px;
  }

  .register-link {
    gap: 10px;
    margin-top: 6px;
  }
}

/* Media query pour les très petits écrans en hauteur */
@media (max-height: 600px) {
  .login-container {
    padding: 5px;
  }

  .login-card {
    padding: 16px;
    max-height: calc(100vh - 20px);
    border-radius: 16px;
  }

  .header-section {
    margin-bottom: 12px;
  }

  .logo-icon {
    font-size: 36px;
    margin-bottom: 6px;
  }

  .header-section h1 {
    font-size: 20px;
    margin-bottom: 3px;
  }

  .subtitle {
    font-size: 12px;
  }

  .login-form {
    gap: 12px;
    margin-bottom: 12px;
  }

  .form-group {
    gap: 4px;
  }

  .form-group label {
    font-size: 11px;
  }

  .form-group input {
    padding: 8px 10px;
    font-size: 12px;
  }

  .btn {
    padding: 8px 16px;
    font-size: 13px;
  }

  .error-banner {
    padding: 6px 10px;
    font-size: 11px;
    margin-bottom: 10px;
  }

  .form-options {
    margin-top: -6px;
    gap: 8px;
  }

  .checkbox-label,
  .forgot-password {
    font-size: 12px;
  }

  .footer-info {
    margin-top: 12px;
  }

  .footer-info p {
    font-size: 10px;
  }

  .register-link {
    gap: 8px;
    margin-top: 4px;
  }

  .register-link span {
    font-size: 12px;
  }
}
</style>
